import { match } from 'ts-pattern';

import { Nullable } from '@ps/utils';

import {
  AdvancedFilters,
  ComputeFeasibilityParams,
  SurveySummary,
} from '@ps/survey-utils';

import {
  SurveyCreationActions,
  SurveyCreationApiActions,
} from '../../audience/survey-creation-audience.actions';
import { SurveyCreationInsightsActions } from '../survey-creation-insights.actions';
import {
  RecalculationData,
  RecalculationTrigger,
} from './survey-creation-recalculation.types';

// True whenever Score/Feasibility should show as pending: either it is
// actually about to recalculate, or it can't right now because manual
// processing is blocking it — and will stay pending until Save or
// toggling manual processing back off clears it.
export function shouldMarkAsPending(action: RecalculationTrigger): boolean {
  return match(action)
    .with(
      { type: SurveyCreationActions.toggleManualProcessing.type },
      (toggled) => !toggled.enabled,
    )
    .otherwise(() => true);
}

// Defines which actions should trigger a recalculation of Score and Feasibility.
export function qualifiesForRecalculation(
  action: RecalculationTrigger,
  enableManualProcessing: boolean,
): boolean {
  return match(action)
    .with(
      { type: SurveyCreationActions.toggleManualProcessing.type },
      (toggled) => !toggled.enabled,
    )
    .with(
      { type: SurveyCreationApiActions.loadSurveyForEditSuccess.type },
      { type: SurveyCreationApiActions.saveSurveySuccess.type },
      { type: SurveyCreationInsightsActions.recalculateFeasibility.type },
      () => true,
    )
    .otherwise(() => !enableManualProcessing);
}

/**
 * Params for feasibility calculation - with or without exclusions.
 * @returns withExclusions as null if there are no exclusions
 */
export function computeFeasibilityParams(
  data: RecalculationData,
  advancedFilters: AdvancedFilters,
  advancedFilterSurveyGroups: SurveySummary[],
  supplierIds: number[],
): {
  base: ComputeFeasibilityParams;
  withExclusions: Nullable<ComputeFeasibilityParams>;
} {
  const base: ComputeFeasibilityParams = {
    surveyId: data.surveyId,
    user: data.user,
    metadata: data.metadata,
    qualifications: data.qualifications,
    quotas: data.quotas,
    supplierIds: supplierIds,
    surveyGroupIds: [],
    isExclusion: false,
    groupingStatuses: [],
  };

  if (!advancedFilterSurveyGroups.length) {
    return { base, withExclusions: null };
  }

  const isExclusion = advancedFilters.type === 'exclude';

  return {
    base,
    withExclusions: {
      ...base,
      surveyGroupIds: advancedFilterSurveyGroups.map((survey) => survey.id),
      isExclusion,
      groupingStatuses: isExclusion
        ? [...advancedFilters.respondentStatuses]
        : [],
    },
  };
}
